import xws from 'api/xws/';
import qs from 'query-string';
import {
  SET_APPT_OPTIONS,
  SET_ADVISOR,
  CLEAR_APPT_OPTIONS
} from './actionTypes';
import {
  ERROR_APPT_OPTION,
  ERROR_NO_CAPACITY
} from 'store/reducers/errors/actionTypes';
import { SET_TRANSPORTATION_OPTION } from '../appointment/actionTypes';
import { CLEAR_APPOINTMENT_SELECTED_DAY } from '../userInteractions/actionTypes';

// Actions
import {
  getMetaVehicleId,
  setTransportationOption
} from 'store/reducers/appointment/actions';
import { ajaxCallbacks } from 'store/reducers/ajaxCall/actions';
import { goTo } from 'store/reducers/navigation/actions';

// Selectors
import {
  getAppointmentParams,
  getMetaVehicleId as getMetaVehicleIdFromState,
  getYear,
  getVehicle,
  getAdvisorId,
  getPreferredAdvisorId,
  getTransportationOption,
  getLeadTimeInDays,
  isModifyAppt
} from 'store/reducers/appointment/selectors';
import { getWebkey } from 'store/reducers/queryParams/selectors';
import {
  getApptOptions,
  getAdvisors,
  getTransportOptions,
  isValetAvailable,
  isTransportationAvailable
} from 'store/reducers/apptOptions/selectors';
import { getPolicyProperty } from 'store/reducers/dealerSettings/selectors';
import { previousPath } from 'store/reducers/navigation/selectors';
import { isLoggedIn } from 'store/reducers/user/selectors';
import {
  getIsQuoteAppt,
  getQuoteConfirmationId,
  getFormattedQuotePackage,
  getFormattedQuoteServices
} from 'store/reducers/quoteAppt/selectors';

// Constants
import { getService } from 'constants/ajax';
import {
  SERVICES_ROUTE,
  APPOINTMENT_ROUTE,
  INDIVIDUAL_SERVICES_ROUTE,
  DROPOFF_ROUTE
} from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';
import { TRANSPORTATION } from 'constants/transportation';

// Utils
import { isEmpty } from 'utils/string';
import { sortArrayOfObjectsBy } from 'utils/callbacks';
import { isObjEmpty } from 'utils/varValidation';

const {
  DROPOFF,
  WAITER,
  SHUTTLE,
  RENTAL,
  VALET,
  LOANER,
  VALETLOANER,
  RIDESHARE
} = TRANSPORTATION;
const { FORWARD, BACKWARD } = NAVIGATION;

const setLabelTransportation = (data, label) => {
  switch (data) {
    case DROPOFF:
      return {
        defaultId: 'c8.transportation.drop.off',
        id: 'c8.drop.off.i.have.a.ride.default'
      };
    case WAITER:
      return {
        defaultId: 'c8.transportation.waiter',
        id: 'c8.drop.off.i.will.wait.at.the.dealership.default'
      };
    case SHUTTLE:
      return {
        defaultId: 'c8.transportation.shuttle',
        id: 'c8.drop.off.i.will.take.the.shuttle.default'
      };
    case LOANER:
      return {
        defaultId: 'c8.transportation.loaner',
        id: 'c8.drop.off.i.will.need.a.loaner.car.default',
        configureString: isEmpty(label) ? '' : label
      };
    case RENTAL:
      return {
        defaultId: 'c8.transportation.rental',
        id: 'c8.drop.off.i.will.need.a.rental.car.default',
        configureString: isEmpty(label) ? '' : label
      };
    case RIDESHARE:
      return {
        id: 'c8.transportation.rideshare',
        defaultId: ''
      };
    default:
      return false;
  }
};

export const getApptAdvisorsAndTranspOptions = () => async (
  dispatch,
  getState
) => {
  if (getApptOptions(getState()) && !isObjEmpty(getApptOptions(getState()))) {
    return;
  }
  const vehicle = getVehicle(getState());
  if (!getMetaVehicleIdFromState(getState())) {
    await dispatch(getMetaVehicleId(vehicle));
  }
  const webkey = getWebkey(getState());
  const apptOptionsService = getService('apptOptions');
  apptOptionsService.setPathVars({ webkey });
  apptOptionsService.setCallBacks(dispatch(ajaxCallbacks()));
  const { autoRecallServices, ...apptParams } = getAppointmentParams(
    getState()
  );
  const loggedIn = isLoggedIn(getState());
  const recallLeadTimeDays = getLeadTimeInDays(getState());
  const isQuoteAppt = getIsQuoteAppt(getState());
  const quoteApptData = isQuoteAppt
    ? {
        quoteConfirmationCode: getQuoteConfirmationId(getState()),
        quoteServicePoint: JSON.stringify(getFormattedQuotePackage(getState())),
        quoteServices: JSON.stringify(getFormattedQuoteServices(getState()))
      }
    : {};

  try {
    const {
      data: { apptOptions }
    } = await xws(apptOptionsService).post(
      apptOptionsService.getPath(),
      qs.stringify({
        ...apptParams,
        recallLeadTimeDays,
        year: getYear(getState()),
        ...(!loggedIn && {
          isValetCustomerBased: false
        }),
        ...quoteApptData
      })
    );
    if (!apptOptions) {
      dispatch({
        type: ERROR_APPT_OPTION,
        payload: true
      });
      return null;
    }
    const transportOptions = apptOptions.transportOptions.map(
      ({ configuration, label, data }) => {
        const labelTransportation = setLabelTransportation(data, label);
        return {
          configuration,
          label: labelTransportation,
          data
        };
      }
    );
    const advisors = apptOptions.advisors.sort(sortArrayOfObjectsBy('label'));
    dispatch(setApptOptions({ transportOptions, advisors }));
    const advisorsOptions = getAdvisors(getState());
    const transportations = getTransportOptions(getState());
    const selectedAdvisorId = getAdvisorId(getState());
    const preferredAdvisorId = getPreferredAdvisorId(getState());
    const findAdvisor = advisorId =>
      advisorsOptions.find(({ data }) => data === advisorId);
    if (!selectedAdvisorId || !findAdvisor(selectedAdvisorId)) {
      const consumerAllowAnyGreeterSel = getPolicyProperty(getState())(
        'consumerAllowAnyGreeterSel'
      );
      const randomIndex = Math.floor(Math.random() * advisorsOptions.length);
      let selectedAdvisor =
        (preferredAdvisorId && findAdvisor(preferredAdvisorId)) ||
        advisorsOptions.find(({ preferred }) => preferred === true);
      if (!selectedAdvisor) {
        selectedAdvisor = consumerAllowAnyGreeterSel
          ? advisorsOptions[0]
          : advisorsOptions[randomIndex];
      }

      if (selectedAdvisor && (advisors && advisors.length > 0)) {
        dispatch({
          type: SET_ADVISOR,
          payload: selectedAdvisor.data
        });
      } else {
        dispatch({
          type: ERROR_NO_CAPACITY,
          payload: true
        });
      }
    }
    const defaultTransportOptionPolicy = getPolicyProperty(getState())(
      'cpMkAptDefaultTransportation'
    );
    const transportationOption = getTransportationOption(getState());
    let defaultTransportation;
    const valetAvailable = isValetAvailable(getState());
    if (
      !transportationOption ||
      !transportations.find(({ data }) => data === transportationOption)
    ) {
      if (
        (transportationOption !== VALETLOANER ||
          transportationOption !== VALET) &&
        !valetAvailable
      ) {
        if (defaultTransportOptionPolicy) {
          defaultTransportation = transportations.find(
            ({ data }) => data === defaultTransportOptionPolicy
          );
        }
        dispatch({
          type: SET_TRANSPORTATION_OPTION,
          payload: defaultTransportation ? defaultTransportation.data : null
        });
      }
    }
    if (transportations.length === 1) {
      if (!defaultTransportation) {
        dispatch({
          type: SET_TRANSPORTATION_OPTION,
          payload: transportations[0].data
        });
      }
    }
  } catch (err) {
    dispatch({
      type: ERROR_APPT_OPTION,
      payload: true
    });
    /* eslint-disable no-console */
    console.log('error'); //
    /* eslint-enable no-console */
    throw err;
  }
};

export const getTransportationOptions = (noNavigation = false) => async (
  dispatch,
  getState
) => {
  const previousTransportationOption = getTransportationOption(getState());
  dispatch({
    type: CLEAR_APPT_OPTIONS
  });
  dispatch({
    type: CLEAR_APPOINTMENT_SELECTED_DAY
  });
  try {
    await dispatch(getApptAdvisorsAndTranspOptions());
    const transportations = getTransportOptions(getState());
    const prevPath = previousPath(getState());
    const selectedTransportationAvailable = isTransportationAvailable(
      getState()
    )(previousTransportationOption);
    if (!selectedTransportationAvailable && isModifyAppt(getState())) {
      await dispatch(setTransportationOption(DROPOFF));
    }
    if (noNavigation) {
      return null;
    }
    if (transportations.length === 1) {
      if (
        prevPath === SERVICES_ROUTE ||
        prevPath === INDIVIDUAL_SERVICES_ROUTE
      ) {
        dispatch(goTo(APPOINTMENT_ROUTE, FORWARD));
        return;
      } else {
        dispatch(goTo(SERVICES_ROUTE, BACKWARD));
      }
    }

    const valetAvailable = isValetAvailable(getState());
    if (!valetAvailable) {
      dispatch(goTo(DROPOFF_ROUTE, FORWARD));
    }
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    /* eslint-enable no-console */
    throw err;
  }
};

export function setApptOptions(apptOptions) {
  return {
    type: SET_APPT_OPTIONS,
    payload: apptOptions
  };
}



// WEBPACK FOOTER //
// ./src/store/reducers/apptOptions/actions.js